// Contractor wizard — invite AND edit. RON-5466 Phase 4. Prototype: `cStep1`–`cStep4`.
//
// Visitors and contractors get SEPARATE wizards for both add and edit; only the list table is
// shared. That is a real requirement, not tidiness: a contractor booking is gated on vetting, and
// routing contractor edits through the visitor wizard meant a host could move a contractor's dates
// past their clearance with nothing to stop them.
//
// CREATE — three steps, not the visitor wizard's four:
//   1 Contractor    look them up on the vetting register (there is nothing to type)
//   2 Visit details when and where — the SAME component the visitor wizard uses
//   3 Review        summary, compliance pack, send-email toggle
//
// EDIT — two steps. The lookup is gone: the contractor is fixed, because changing WHO is coming
// makes it a different visit rather than an edit of this one. They are shown read-only instead.
//
// The visitor wizard's "Visitor type" step is dropped in both modes: VIP is a hosting courtesy for
// a guest, and promoting a contractor to VIP has no meaning to reception or the kiosk. `type` is
// fixed to standard.
//
// The vetting gate is checked at every step transition AND again before submit, because the dates
// it depends on are chosen between those points. A contractor cleared until 15 September stops
// being bookable the moment the visit is moved to October.

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  EMAIL_PATTERN,
  formatDateToEEEddMMM,
} from "../../../utils/CommonUtils.js";
import {
  LOOKUP_OUTCOME,
  VETTING_STATE,
  VISIT_KIND,
  VISIT_TYPE,
  contractorBookingBlock,
  hasExpiredDocuments,
  isContractorBlocked,
  isContractorProvisional,
} from "../visitorConstants.js";
import { isSameOrBefore } from "../visitorDateUtils.js";
import ContractorLookupStep from "./ContractorLookupStep.jsx";
import ContractorVettingCard from "./ContractorVettingCard.jsx";
import VisitDetailsStep from "./VisitDetailsStep.jsx";
import VisitorDetailRow from "./VisitorDetailRow.jsx";
import VisitorEmailPreview from "./VisitorEmailPreview.jsx";
import ContractorWorkStep, {
  DURATION_LABEL_KEYS,
  defaultDuration,
} from "./ContractorWorkStep.jsx";
import VisitorResourceSummary from "./VisitorResourceSummary.jsx";
import VisitorStepBar from "./VisitorStepBar.jsx";

const CREATE_STEPS = [
  "react_visitors_step_contractor",
  "react_visitors_step_visit",
  "react_visitors_step_work",
  "react_visitors_step_review",
];

// No lookup when editing, so the bar shows the steps that exist rather than a permanently
// completed first one the host can never return to.
//
// WORK DETAILS IS EDITABLE AGAIN — 16-October-2026.
//
// A short history, because the reasoning matters more than the outcome:
//
//   10-Oct  added to both flows. A job that changes should take its description with it.
//   12-Oct  removed from EDIT. `work_description`, `areas` and `personnel` appeared exactly
//           once in RON-6002 — in the create table. Accepted on POST, not returned on GET, not
//           in the PATCH changeable list. So an edit would have shown EMPTY fields for a visit
//           that has them and dropped whatever was typed.
//   14-Oct  Laravel built the read and the PATCH (answers-to-frontend-8), plus `duration` and
//           `supervisor` columns. All five now round-trip. Their note says it plainly: "You can
//           put Work details back into the edit flow."
//
// So this is the 10-October intent restored, now that the API can carry it.
const EDIT_STEPS = [
  "react_visitors_step_visit",
  "react_visitors_step_work",
  "react_visitors_step_review",
];

const DEFAULT_ARRIVAL = "09:00";
// `DEFAULT_EXPECTED_OUT` was here until 07-October-2026 — removed with the field itself; see
// `VisitDetailsStep.jsx`.

const defaultBuildingId = (site) => {
  if (!site?.buildings?.length) return null;

  const preferred = site.buildings.find((building) => building.isDefault);

  return (preferred || site.buildings[0]).id;
};

const ContractorInviteWizard = ({
  sites,
  todayISO,
  hostName,
  canAssignDesk,
  canAssignParking,
  isSubmitting,
  isSearching,
  contractor,
  hasSearched,
  lookupResult,
  lookupError,
  onLookup,
  onResetLookup,
  onRequestVetting,
  onSubmit,
  onCancel,
  mode = "create",
  initialVisit = null,
}) => {
  const { t } = useTranslation("common");

  const isEdit = mode === "edit" && Boolean(initialVisit);
  const steps = isEdit ? EDIT_STEPS : CREATE_STEPS;

  // Step indices differ by mode, so they are derived rather than a fixed enum. LOOKUP is -1 when
  // editing, which no step can equal — that is what removes it from the flow.
  const LOOKUP = isEdit ? -1 : 0;
  const VISIT = isEdit ? 0 : 1;
  // -1 when editing, the same trick as LOOKUP: a value no step can equal, which takes it out of
  // the flow without the render branches needing to know why.
  const WORK = isEdit ? 1 : 2;
  const REVIEW = isEdit ? 2 : 3;

  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  // Editing defaults to NOT re-sending: the contractor already has the invite, and a second copy
  // for a corrected arrival time should be a deliberate choice.
  // ON by default for BOTH invite and edit, as of 24-October-2026.
  //
  // This was `!isEdit` — off when editing — which was correct while an edit sent no email at all:
  // the flag never reached the wire. §7 added `send_update`, which the SERVER defaults to `true`,
  // and the prototype draws the toggle on. A host who wants to edit quietly turns it off.
  const [sendEmail, setSendEmail] = useState(true);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState(() =>
    isEdit
      ? {
          startDate: initialVisit.startDate,
          endDate: initialVisit.endDate,
          arrivalTime: initialVisit.arrivalTime,
          // Resolved from names once `sites` lands — see below.
          siteId: null,
          buildingId: null,
          host: initialVisit.host || hostName,
          workDescription: initialVisit.workDescription || "",
          areas: initialVisit.areas || "",
          duration:
            initialVisit.duration ||
            defaultDuration(initialVisit.startDate, initialVisit.endDate),
          supervisor: initialVisit.supervisor || "",
          // A string in form state, coerced once on submit — see the note in ContractorWorkStep.
          personnel: String(initialVisit.personnel ?? 1),
          resources: {},
        }
      : {
          startDate: todayISO,
          endDate: todayISO,
          arrivalTime: DEFAULT_ARRIVAL,
          siteId: sites[0]?.id ?? null,
          buildingId: defaultBuildingId(sites[0]),
          host: hostName,
          workDescription: "",
          areas: "",
          duration: defaultDuration(todayISO, todayISO),
          supervisor: "",
          personnel: "1",
          resources: {},
        }
  );

  // A visit stores its site and building by NAME — that is what the list and detail screens
  // display — while the wizard works in ids. Bridge the two once sites are available.
  useEffect(() => {
    if (!isEdit || !sites.length || form.siteId) return;

    const site = sites.find((candidate) => candidate.name === initialVisit.site) || null;
    const building =
      site?.buildings?.find((candidate) => candidate.name === initialVisit.building) ||
      null;

    setForm((current) => ({
      ...current,
      // A site that no longer exists leaves the picker empty rather than guessing another one.
      siteId: site?.id ?? null,
      buildingId: building?.id ?? (site ? defaultBuildingId(site) : null),
    }));
  }, [isEdit, initialVisit, sites, form.siteId]);

  const selectedSite = useMemo(
    () => sites.find((site) => site.id === form.siteId) || null,
    [sites, form.siteId]
  );

  const selectedBuilding = useMemo(
    () =>
      selectedSite?.buildings?.find((building) => building.id === form.buildingId) ||
      null,
    [selectedSite, form.buildingId]
  );

  // THE RECORD THE GATE RUNS AGAINST.
  //
  // Creating: whatever the lookup returned. Editing: the live register record when the caller has
  // fetched it, because vetting changes between booking and editing — someone cleared in August may
  // be blocked by October, and the copy stored on the visit would not know.
  //
  // Falling back to the visit's own snapshot matters: it means an edit is still gated when the
  // register cannot be reached, rather than silently losing the check.
  const effectiveRecord = useMemo(() => {
    if (contractor) return contractor;
    if (!isEdit) return null;

    return {
      id: initialVisit.contractorId ?? null,
      name: initialVisit.name,
      email: initialVisit.email,
      company: initialVisit.company,
      trade: initialVisit.trade,
      // A snapshot, not a register read: the visit records only WHETHER it was provisional, not
      // which of the three in-flight states it was in. `waiting` is the neutral stand-in — it
      // earns the same badge colour and the same booking outcome, and the real state is re-read
      // from the register whenever that is reachable.
      vettingState: initialVisit.isProvisional
        ? VETTING_STATE.WAITING
        : VETTING_STATE.VETTED,
      vettedUntil: initialVisit.vettedUntil ?? null,
      // The compliance pack lives on the register, not the visit, so a snapshot has none to show.
      documents: [],
    };
  }, [contractor, isEdit, initialVisit]);

  // One source of truth for "may this be booked", shared by every step guard and the submit guard.
  // The register's own verdict (§4.3), used in place of deriving one. `null` on an older
  // reply or on an edit that fell back to the visit snapshot — `contractorBookingBlock`
  // says what happens then.
  const outcome = lookupResult?.outcome ?? null;

  // The OPEN vetting request for this email, when the register knows of one (§4.3's
  // `lookup.request`). Shown on the cards that offer to make one, so the host is not asking into
  // a void they cannot see.
  const openRequest = lookupResult?.request ?? null;

  // THE DATE IS WITHHELD ON STEP 1, and that is not a detail.
  //
  // `form.endDate` defaults to today, so the clearance check was running on the lookup step with
  // a date the host had not chosen yet — and a contractor the register returns as `proceed` could
  // still be refused there by our own arithmetic. Harmless while we derived both answers from the
  // same field; a visible contradiction now that the register states one of them.
  //
  // The prototype puts this check in `CStep2` (`dateBlocked`) for the same reason: until the dates
  // are picked there is no date to be cleared for. From step 2 on, it applies as before, and on an
  // edit (`LOOKUP` is -1) it always applies.
  const bookingBlock = contractorBookingBlock(
    effectiveRecord,
    step === LOOKUP ? null : form.endDate,
    outcome
  );

  // The register answered, and nobody is registered under that address. Derived here rather than
  // in the lookup step because the ACTION it earns ("Request vetting") lives in the wizard footer
  // — the prototype's `CStep1` puts it there, replacing Next, for the same reason the expiry case
  // does: Next has nothing to advance to, so the only useful button is the one that moves this on.
  const lookupMissed =
    step === LOOKUP && hasSearched && !contractor && !errors.email && !lookupError;

  // On the register, and their clearance has already lapsed. A DEAD END until now: the vetting
  // card said "blocked and cannot be invited", Next was disabled, and that was the whole screen.
  // The prototype offers 'Request re-vetting' here, which is the one thing that actually unblocks
  // the host — and it is the same request the expiry case below already sends.
  const lookupExpired =
    step === LOOKUP &&
    (outcome
      ? outcome === LOOKUP_OUTCOME.BLOCKED_EXPIRED
      : isContractorBlocked(effectiveRecord));

  // Only for the EXPIRY case. A blocked contractor is a different problem with a different
  // answer — "contact the compliance admin team" — and re-vetting is not what they need.
  const expiryBannerText =
    bookingBlock === "react_visitors_vetting_expired_message" && effectiveRecord?.vettedUntil
      ? t("react_visitors_vetting_expired_banner")
          .replace("%n", effectiveRecord.name || "")
          .replace("%c", formatDateToEEEddMMM(effectiveRecord.vettedUntil))
          .replace("%d", formatDateToEEEddMMM(form.startDate))
      : null;

  const setField = (key, value) => {
    setForm((current) => {
      const next = { ...current, [key]: value };

      if (key === "startDate" && !isSameOrBefore(value, next.endDate)) {
        next.endDate = value;
      }
      if (key === "siteId") {
        const site = sites.find((candidate) => candidate.id === value) || null;
        next.buildingId = defaultBuildingId(site);
      }

      return next;
    });

    setErrors({});
  };

  // Changing the email after a hit invalidates the record on screen — otherwise the host could
  // search one contractor, retype a different address and book the first one.
  const changeEmail = (value) => {
    setEmail(value);
    setErrors({});
    if (contractor || hasSearched) onResetLookup();
  };

  const lookup = () => {
    const trimmed = email.trim();

    if (!trimmed) {
      setErrors({ email: "react_visitors_error_email_required" });
      return;
    }
    if (!EMAIL_PATTERN.test(trimmed)) {
      setErrors({ email: "react_visitors_error_email_invalid" });
      return;
    }

    setErrors({});
    onLookup(trimmed);
  };

  const validateStep = (candidate) => {
    if (candidate === LOOKUP) {
      if (!contractor) return { email: "react_visitors_error_lookup_first" };
      if (bookingBlock) return { email: bookingBlock };

      return null;
    }

    if (candidate === VISIT) {
      const found = {};

      if (!form.host.trim()) found.host = "react_visitors_error_host";
      // The date gate, now that real dates have been chosen. This is the check that makes
      // contractor edit safe — the visitor wizard has no equivalent.
      if (bookingBlock) found.endDate = bookingBlock;

      return Object.keys(found).length ? found : null;
    }

    // Step 3 has no required fields. The prototype marks none, and a host who does not yet know
    // which rooms the job touches should still be able to book the visit.
    if (candidate === WORK) return null;

    return null;
  };

  const goNext = () => {
    const found = validateStep(step);

    if (found) {
      setErrors(found);
      return;
    }

    setStep((current) => Math.min(current + 1, REVIEW));
  };

  const goBack = () => {
    if (step === 0) {
      onCancel();
      return;
    }

    setErrors({});
    setStep((current) => Math.max(current - 1, 0));
  };

  const submit = () => {
    // Re-checked at the last moment: the dates may have moved since the earlier check passed.
    if (bookingBlock) {
      setErrors({ endDate: bookingBlock });
      setStep(VISIT);
      return;
    }

    onSubmit({
      kind: VISIT_KIND.CONTRACTOR,
      // Carried so an edit can PATCH the right row instead of matching on name.
      visitId: initialVisit?.id ?? null,
      contractorId: effectiveRecord.id,
      visitors: [
        {
          name: effectiveRecord.name,
          email: effectiveRecord.email,
          company: effectiveRecord.company,
        },
      ],
      trade: effectiveRecord.trade,
      // Reception must not admit a provisionally-vetted contractor, so the flag travels with the
      // visit rather than being re-derived from the register later.
      isProvisional: isContractorProvisional(effectiveRecord),
      vettedUntil: effectiveRecord.vettedUntil,
      site: selectedSite,
      building: selectedBuilding,
      sendEmail,
      type: VISIT_TYPE.STANDARD,
      ...form,
    });
  };

  // Who is coming. Read-only in both modes — on create the register supplied it, on edit it is
  // fixed — so the same card serves the edit visit step and both reviews.
  const renderContractorCard = () => (
    <div className="visitor-card">
      <p className="visitor-card__title">{t("react_visitors_contractor_found")}</p>
      <VisitorDetailRow label={t("name")} value={effectiveRecord.name} />
      <VisitorDetailRow label={t("company")} value={effectiveRecord.company} />
      {effectiveRecord.trade && (
        <VisitorDetailRow
          label={t("react_visitors_field_trade")}
          value={effectiveRecord.trade}
        />
      )}
    </div>
  );

  const renderStep = () => {
    if (step === LOOKUP) {
      return (
        <ContractorLookupStep
          email={email}
          onEmailChange={changeEmail}
          onLookup={lookup}
          isSearching={isSearching}
          hasSearched={hasSearched}
          contractor={contractor}
          // A validation problem from this component wins over one reported by the lookup itself,
          // because the user just acted on the field.
          error={errors.email || lookupError}
          todayISO={todayISO}
          openRequest={openRequest}
        />
      );
    }

    if (step === VISIT) {
      return (
        <>
          {/* ALWAYS, not only when editing — corrected 15-October-2026 (parity map V1).
              The prototype's `CStep2` opens with this card in both cases, and the reason is the
              same either way: picking dates with no reminder of whose visit it is invites
              booking the right days for the wrong contractor. It was conditional on `isEdit`
              on the reasoning that a host who just searched for somebody remembers who — which
              is true for the second after the search, and not after a phone call. */}
          {renderContractorCard()}

          {/* THE CLEARANCE BANNER (parity map V8). The block itself is not new — step 2 has
              always refused to advance — but it said so with a field error under End date, which
              reads as a typo rather than a vetting problem, and offered no way out.

              The prototype names all three dates the host has to reconcile: who, when their
              clearance ends, and when the visit is. That is what makes it actionable: the host
              can move the visit or ask for re-vetting, and both choices are on screen. */}
          {bookingBlock && expiryBannerText && (
            <div className="visitor-card visitor-card--warning">
              <p className="visitor-card__title">
                {t("react_visitors_vetting_expired_title")}
              </p>
              <p className="visitor-card__hint">{expiryBannerText}</p>
            </div>
          )}

          <VisitDetailsStep
            form={form}
            sites={sites}
            errors={errors}
            onField={setField}
            canAssignDesk={canAssignDesk}
            canAssignParking={canAssignParking}
          />
        </>
      );
    }

    // Step 3 — the prototype's `CStep3`. Contractor only; the visitor wizard has no equivalent.
    if (step === WORK) {
      return <ContractorWorkStep form={form} errors={errors} onField={setField} />;
    }

    return (
      <>
        {renderContractorCard()}

        <div className="visitor-card">
          <p className="visitor-card__title">{t("react_visitors_visit_title")}</p>
          {/* Formatted, not the raw ISO the form stores — every other screen in the module shows
              a date this way, and "2026-08-24" on a review step is not something to read back. */}
          <VisitorDetailRow
            label={t("react_visitors_field_start_date")}
            value={formatDateToEEEddMMM(form.startDate)}
          />
          <VisitorDetailRow
            label={t("react_visitors_field_end_date")}
            value={formatDateToEEEddMMM(form.endDate)}
          />
          <VisitorDetailRow
            label={t("react_visitors_field_arrival")}
            value={form.arrivalTime}
          />
          <VisitorDetailRow
            label={t("react_visitors_field_site")}
            value={selectedSite?.name || "—"}
          />
          {selectedBuilding && (
            <VisitorDetailRow
              label={t("react_visitors_field_building")}
              value={selectedBuilding.name}
            />
          )}
          <VisitorDetailRow label={t("react_visitors_field_host")} value={form.host} />
        </div>

        {/* The prototype's Card 'Work' on `CStep4`. An em dash where a field was left blank, as
            the prototype does — the row stays so the host can see what they did not fill in. */}
        <div className="visitor-card">
          <p className="visitor-card__title">{t("react_visitors_work_review_title")}</p>
          <VisitorDetailRow
            label={t("react_visitors_field_work_description")}
            value={form.workDescription || "—"}
          />
          <VisitorDetailRow
            label={t("react_visitors_field_work_areas")}
            value={form.areas || "—"}
          />
          <VisitorDetailRow
            label={t("react_visitors_field_work_duration")}
            value={t(DURATION_LABEL_KEYS[form.duration] || "react_visitors_duration_full_day")}
          />
          <VisitorDetailRow
            label={t("react_visitors_field_work_personnel")}
            value={form.personnel || "1"}
          />
          <VisitorDetailRow
            label={t("react_visitors_field_work_supervisor")}
            value={form.supervisor || "—"}
          />
        </div>

        {/* Step 2 offers desk and parking to contractors too, so the review has to confirm them —
            otherwise a host books a desk and never sees it again. */}
        <VisitorResourceSummary
          form={form}
          canAssignDesk={canAssignDesk}
          canAssignParking={canAssignParking}
        />

        {/* Repeated here with the visit dates in hand, so an expiry that only bites for THESE
            dates is visible at the moment of commitment. */}
        <ContractorVettingCard
          contractor={effectiveRecord}
          todayISO={todayISO}
          endDateISO={form.endDate}
        />

        {/* Lapsed paperwork does not block the booking — the vetting does that — but reception
            should not discover it on the day. */}
        {hasExpiredDocuments(effectiveRecord, todayISO) && (
          <div className="visitor-card visitor-card--warning">
            <p className="visitor-vetting__reason">
              {t("react_visitors_docs_expired_warning")}
            </p>
          </div>
        )}

        <div className="visitor-card">
          <label className="visitor-toggle">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={() => setSendEmail((current) => !current)}
              className="visitor-toggle__input"
            />
            <span className="visitor-toggle__text">
              <span className="visitor-toggle__label">
                {t(isEdit ? "react_visitors_send_update_email" : "react_visitors_send_email")}
              </span>
              <span className="visitor-toggle__hint">
                {/* Inviting sends `visit_confirmation`; editing sends `visit_updated`, which only
                    goes when something the visitor SEES changed. Different email, different rule,
                    so different words. */}
                {t(
                  isEdit
                    ? "react_visitors_send_update_email_hint"
                    : "react_visitors_send_email_hint"
                )}
              </span>
            </span>
            <span className="visitor-toggle__track" aria-hidden="true">
              <span className="visitor-toggle__knob" />
            </span>
          </label>

          {/* A contractor gets the same invite email a visitor does, so the host should be able to
              see it before sending. Same component, with the contractor as the single recipient. */}
          {sendEmail && (
            <div className="visitor-email-wrap">
              <p className="visitor-card__subtitle">
                {t("react_visitors_email_preview")}
              </p>
              <VisitorEmailPreview
                form={form}
                site={selectedSite}
                building={selectedBuilding}
                visitors={[
                  {
                    name: effectiveRecord.name,
                    email: effectiveRecord.email,
                    company: effectiveRecord.company,
                  },
                ]}
                // Present only here, never on the visitor wizard — it is what switches the
                // preview to the contractor body: the Work line, and a closing paragraph that
                // withholds the QR code when vetting is not yet complete.
                //
                // `isContractorProvisional` rather than a fresh comparison, so this agrees with
                // the badge and the warning on the step above it. It covers the three in-flight
                // states; an EXPIRED contractor never reaches this screen, because
                // `contractorBookingBlock` stops the booking before review.
                contractor={{
                  isProvisional: isContractorProvisional(effectiveRecord),
                  vettedUntil: effectiveRecord.vettedUntil ?? null,
                }}
              />
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="visitor-wizard">
      <h2 className="module-title mb-3">
        {t(
          isEdit
            ? "react_visitors_edit_contractor"
            : "react_visitors_invite_a_contractor"
        )}
      </h2>

      {/* Purple, matching the prototype's `CStepBar`. The visitor wizard's `StepBar` is the
          same component in the house green — contractor flows are purple throughout this module
          (`--visitor-contractor`, already the prototype's #3c3489). */}
      <VisitorStepBar steps={steps} current={step} tone="contractor" />

      {renderStep()}

      <div className="visitor-wizard__actions">
        <button
          type="button"
          className="visitor-btn visitor-btn--secondary"
          onClick={goBack}
          disabled={isSubmitting}
        >
          {step === 0 ? t("react_visitors_back_to_list") : t("react_visitors_back")}
        </button>

        {step === REVIEW ? (
          <button
            type="button"
            className="visitor-btn visitor-btn--secondary"
            onClick={submit}
            disabled={isSubmitting}
          >
            {t(isEdit ? "react_visitors_save_changes" : "react_visitors_submit")}
          </button>
        ) : lookupMissed && onRequestVetting ? (
          /* Parity: the prototype's `CStep1` footer swaps Next for 'Request vetting' when the
             register has no record. It used to sit inside the not-found card as a quiet secondary
             button, which put the only available action somewhere the eye does not look for one —
             the footer is where every other step keeps its forward action.

             Conditional on `onRequestVetting` for the same reason as the expiry swap below: with
             no endpoint the handler is withheld, and this falls back to a disabled Next. */
          <button
            type="button"
            className="visitor-btn visitor-btn--secondary"
            // No `needed_by`: the host has not chosen dates yet. The server classifies this
            // from the register instead, and for somebody not on it that is `unknown`.
            onClick={() => onRequestVetting(email.trim())}
            disabled={isSubmitting}
          >
            {t("react_visitors_request_vetting")}
          </button>
        ) : lookupExpired && onRequestVetting ? (
          /* The expired contractor's way out. Same request, different word on the button and a
             different reason code — 'request vetting' would be wrong for somebody the company has
             vetted before and will be renewing. */
          <button
            type="button"
            className="visitor-btn visitor-btn--secondary"
            onClick={() => onRequestVetting(effectiveRecord?.email || email.trim())}
            disabled={isSubmitting}
          >
            {t("react_visitors_request_re_vetting")}
          </button>
        ) : expiryBannerText && onRequestVetting ? (
          /* THE BUTTON SWAP (parity map V8). When the clearance expires before the visit, the
             prototype replaces Next with 'Request re-vetting' — because Next has nothing to do,
             and the only useful action left is the one the banner above describes.

             Conditional on `onRequestVetting` for the same reason as the lookup step's button:
             there is no vetting endpoint on the real API, so the handler is withheld and this
             falls back to a disabled Next rather than throwing on click. */
          <button
            type="button"
            className="visitor-btn visitor-btn--secondary"
            // THE DATE TRAVELS. §4.10: "send the visit date as needed_by when you have one —
            // it is what makes a 'clearance ends before the visit' request". This is the one
            // branch that has one, and without it the server sees an ordinary renewal and
            // the compliance team loses the deadline they are working to.
            onClick={() =>
              onRequestVetting(effectiveRecord?.email || email.trim(), form.startDate)
            }
            disabled={isSubmitting}
          >
            {t("react_visitors_request_re_vetting")}
          </button>
        ) : (
          <button
            type="button"
            className="visitor-btn visitor-btn--secondary"
            onClick={goNext}
            // On the lookup step: nothing to advance to until the register has answered, and
            // nothing to advance TO for a blocked contractor — the red card above already says why.
            //
            // On the VISIT step it is disabled rather than swapped when there is no vetting
            // endpoint: a button that refuses on click is worse than one that plainly cannot be
            // pressed, and the banner above says why.
            disabled={
              (step === LOOKUP && (!contractor || Boolean(bookingBlock))) ||
              Boolean(expiryBannerText)
            }
          >
            {t("react_visitors_next")}
          </button>
        )}
      </div>
    </div>
  );
};

export default ContractorInviteWizard;
